import {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  AnalyticsService,
  LocalizationService,
  TokenService,
} from '@services';
import { ApiEvents, ApiEventsNamesEnum } from './ApiEvents';
import type {
  ApiErrorDataType,
  AxiosErrorResponseType,
} from './types';

/**
 * Request fulfilled interceptor
 * Automatically attaches JWT token and current selected language
 * to each request
 * @param {InternalAxiosRequestConfig} requestConfig
 */
export async function requestFulfilled(
  requestConfig: InternalAxiosRequestConfig,
) {
  // To avoid type error we need to default headers with an empty object
  requestConfig.headers = requestConfig.headers ?? {};

  // We retrieving tokens from TokenService because
  // each request must be sent with up-to-date token
  const tokens = await TokenService.get();

  // Set up-to-date token for each request if tokens exist
  if (tokens) {
    requestConfig.headers.Authorization = `Bearer ${tokens.accessToken}`;
  }

  // Set analytics session id
  requestConfig.headers['Session-Id'] =
    AnalyticsService.getSessionId();

  // Set up-to-date language for each request to localize our responses
  requestConfig.headers['Accept-Language'] =
    LocalizationService.language;

  return requestConfig;
}

/**
 * Response fulfilled interceptor
 * @param {AxiosResponse<any, any>} response
 */
export async function responseFulfilled(
  response: AxiosResponse<any, any>,
) {
  // Any status code that lie within the range of 2xx cause this function to trigger
  // Do something with response data
  return response;
}

/**
 * Response rejected interceptor
 * Automatically attaches Network Error flag and will validate errorCode
 * @param {AxiosError<ApiErrorDataType>} error
 */
export async function responseRejected(
  error: AxiosError<ApiErrorDataType>,
) {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error

  // Add isNetworkError flag
  (error as AxiosErrorResponseType).isNetworkError =
    error.message === 'Network Error';

  // We retrieving token from TokenService because
  // we should check errorCode for authorized user
  const tokens = await TokenService.get();

  // When user is not authorized, we will not check errorCode
  if (!tokens) {
    return Promise.reject(error);
  }

  // When user is authorized
  // and we catch some of errorCode below, we need to sign out user
  switch (error.response?.data.errorCode) {
    case 85: // User is banned
    case 86: // User is removed
    case 87: // User plan subscription not renewed
    case 91: // User is deleted
    case 96: // Refresh token is invalid or expired
      ApiEvents.emit(ApiEventsNamesEnum.Logout, error);
  }

  return Promise.reject(error);
}
